// ============================================================================
// src/app/data/useRouteMutation.ts — the write runtime, on TanStack
// ============================================================================
// The sibling of useRouteResource. That hook is `useQuery` over a catalog
// resource; this is `useMutation` over a catalog mutation, and the reason it
// exists is the one member the read side has no equivalent for: `invalidates`.
//
// WHAT THIS BUYS THAT A BARE `await sb.from(...).update(...)` DOES NOT
//
// Not error handling — the call sites already do that, and this hook
// deliberately does not take it away. Not retry — queryClient.ts already set
// `mutations: {retry: false, networkMode: 'always'}` "so the FIRST one that
// does inherits this lane's parity posture", and this is that first one; it
// adds no per-call override, for exactly the reason routeResourceQuery gives
// for the read side.
//
// What it buys is that CACHE INVALIDATION STOPS BEING SOMETHING A CALL SITE
// REMEMBERS. Today a write is followed by `await loadAll()` — the read
// resource's refetch — which refreshes the one resource the writing surface
// happened to be mounted on, and nothing else.
//
// Be precise about what that costs, because the dramatic version is wrong (see
// routeMutationCatalog's header, which corrects it at length). `staleTime: 0`
// and `refetchOnMount: true` mean a resource that unmounts and remounts
// refetches anyway, so the app is not serving pre-write rows for the length of
// gcTime. What is genuinely missed is narrower:
//
//   * a resource whose observer STAYS MOUNTED across the write never refreshes,
//     because refetchOnWindowFocus / refetchOnReconnect / refetchInterval are
//     all deliberately off and no other trigger exists;
//   * a retained-but-unmounted resource shows one stale frame on its next mount.
//
// Both are bounded. Neither is checked, and that is the actual problem: the
// only thing standing between a write and a stale panel is whether somebody
// remembered. SheepBatchesView's create remembered nothing at all — it
// navigated away instead.
//
// Here the entry declares which resources it invalidates and this hook applies
// them, all of them, every time. The declaration is checked against the read
// catalog by tests/static/route_mutation_catalog_static.test.js, so an entry
// that under-declares fails by name in CI instead of becoming a panel nobody
// noticed had stopped updating.
//
// ---------------------------------------------------------------------------
// WHY PREFIX INVALIDATION IS CORRECT HERE AND NOT A SHORTCUT
//
// `invalidateQueries({queryKey: [resourceId]})` is a PARTIAL match: it selects
// every cache entry whose key STARTS WITH that segment. resourceKey builds
// `[resourceId, scopeSegment, normalizedParams]`, and routeDataCatalog says
// what that nesting is for in as many words — "so `params` stays at a fixed
// index and a prefix match on [resourceId] still selects every scope's entries
// for invalidation". This is the consumer that clause was written for.
//
// Two consequences worth stating rather than discovering:
//
//   IT CROSSES SCOPES ON PURPOSE. A signed-out user's retained entry is marked
//   stale too. That is right: staleness is not a read, nothing is served from
//   it, and the alternative — rebuilding the writer's own scope segment here to
//   narrow the match — would put a second, unreviewed key builder at every
//   write site, which is the exact defect UNRESOLVED_RESOURCE_KEY exists to
//   prevent on the read side.
//
//   IT CROSSES PARAMS ON PURPOSE. Invalidating `sheep.batch.detail` marks
//   EVERY batch's detail entry stale, not just the one that was written. Also
//   right, and not merely convenient: a delete that clears straggler
//   `sheep.processing_batch_id` links changes rows a different batch's detail
//   read would return.
//
// ---------------------------------------------------------------------------
// WHY `run` AWAITS THE INVALIDATION
//
// Because `await loadAll()` did, and preserving that ordering is what keeps a
// conversion honest. TanStack resolves `mutateAsync` only after `onSuccess`
// settles, so awaiting `run` means "the write landed AND every declared
// resource with a live observer has been given its refetch" — the same promise
// the call sites were already awaiting, widened from one resource to the
// declared set. It never means the WRITE is in doubt; see the next section.
//
// The widening is not free and is not hidden: a surface that previously
// navigated away IMMEDIATELY after its write now waits for the refetch of the
// resources it is still mounted on. Only ACTIVE observers refetch — an
// invalidated entry nobody is watching is merely marked stale — so in practice
// that is one already-warm list, not the whole declared set. This is the one
// intentional behaviour delta of the pilot conversion and it is called out in
// the lane report rather than left for a reviewer to find.
//
// ---------------------------------------------------------------------------
// WHY THE INVALIDATION CANNOT FAIL THE WRITE
//
// It awaits, but it does not JUDGE. By the time onSuccess runs the write has
// already COMMITTED — the row is in the database and the operator's edit is
// safe — and TanStack routes a throw from onSuccess into the SAME catch that
// handles a failed mutationFn (query-core's Mutation.execute awaits onSuccess
// inside the try; the catch dispatches {type:'error'} and rethrows). So an
// unguarded onSuccess turns a committed write into a REJECTED mutateAsync, and
// the call site reports "Save failed" over a row that saved. That is not a
// smaller bug than a lost write. It is a worse one, because the operator
// retypes an edit that is already there and a unique index then refuses the
// duplicate.
//
// The repository wrote this rule down before the seam existed. EggDailysView's
// post-write refresh requires that a transient shared-projection read "must
// preserve last-good dashboard data instead of producing an unhandled
// rejection or retroactively reporting a failed save". The seam now holds that
// line once, for every surface converted onto it, instead of each call site
// remembering — the same argument that put invalidation here in the first
// place.
//
// WHAT IS AND IS NOT NON-FATAL. The WRITE's own failure rejects exactly as it
// did, with error.code intact; nothing below touches the mutationFn or the
// error it throws. Only the POST-write invalidation is swallowed. The cost is
// bounded and is already named above: a resource with a live observer may keep
// showing pre-write rows until its next mount, and staleTime: 0 means that
// mount refetches. The buy is that "the save succeeded" means the save
// succeeded.
//
// IT IS NOT SILENT. A swallowed failure nobody can see is how a fail-open
// survives a code review; this one warns, naming the mutation id and every
// reason, so a systematically failing invalidation shows up in a console
// rather than as a panel that quietly stopped updating.
// ============================================================================

import {useCallback, useMemo, useRef, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import type {QueryClient} from '@tanstack/react-query';

import {sb} from '../../lib/supabase.js';
import {getMutation, mutationInvalidationKeys} from './routeMutationCatalog.js';
// The client type is owned by src/lib/supabaseTypes.ts, NOT by the library —
// tests/static/supabase_client_owner_static.test.js counts TEXT occurrences of
// the '@supabase/supabase-js' specifier and an erased `import type` still
// counts. Same note as routeResourceQuery.ts.
import type {SupabaseClient} from '../../lib/supabaseTypes.js';

export type UseRouteMutationOptions = {
  client?: SupabaseClient;
};

export type UseRouteMutationReturn<Vars, Data> = {
  /**
   * Send the write, then apply every declared invalidation.
   *
   * Rejects exactly as the catalog entry's `run` does — this hook adds no
   * translation. An entry that throws on a PostgREST error (the two direct
   * table writers) rejects with that error's raw message; an entry wrapping an
   * RPC that reports refusal in its envelope (`sheep.batch.delete`) resolves
   * with the envelope. Call sites keep the branch they already had.
   */
  run: (vars: Vars) => Promise<Data>;
  /** True from the moment `run` is called until the invalidations settle. */
  pending: boolean;
  /** The last rejection, or null. Present for parity with the read projection. */
  error: unknown;
  /** Clear `error`/`pending` — for a surface that closes and reopens a form. */
  reset: () => void;
};

/**
 * What routeMutationOptions needs from a QueryClient: the one method it calls.
 * Narrow on purpose, so a test can drive this with a stub that fails on demand
 * without standing up a cache it does not otherwise use.
 */
export type RouteMutationInvalidator = Pick<QueryClient, 'invalidateQueries'>;

export type RouteMutationOptionsInput = {
  /** A catalog mutation id. Resolved here, so an unknown one throws by name. */
  mutationId: string;
  /** The Supabase client handed to the entry's own `run`. */
  client: SupabaseClient;
  /** The cache the declared invalidations are applied to. */
  queryClient: RouteMutationInvalidator;
};

/**
 * The `useMutation` options for one catalog write, as a plain value.
 *
 * Lifted out of the hook body for the reason mutationInvalidationKeys already
 * states in as many words: this repository's vitest environment is `node` with
 * no renderer, so anything left inside a hook body cannot be tested at all. As
 * a value, the whole write-then-invalidate pipeline can be handed to a real
 * MutationObserver — which is exactly how routeResourceQuery.test.ts drives the
 * read side — so "a failed invalidation must not reject a committed write" is
 * asserted as BEHAVIOUR instead of pinned as source text.
 *
 * Nothing is decided here that was not decided before. This is the same object
 * literal the hook used to pass inline, plus the guard it was missing.
 */
export function routeMutationOptions<Vars, Data>({mutationId, client, queryClient}: RouteMutationOptionsInput) {
  // Throws by name on an unknown id — called during render from the hook below,
  // exactly as getOneShotResource is. A mistyped mutation id must not degrade
  // into a write that silently never happens.
  const entry = getMutation(mutationId);

  return {
    // Not a cache key — mutations have no cache. It is what makes this write
    // identifiable to `useMutationState`/`isMutating` and in devtools, and it
    // is the entry's own id so the two can never disagree.
    mutationKey: [entry.id],
    mutationFn: (vars: Vars) => (entry.run as (sb: SupabaseClient, v: Vars) => Promise<Data>)(client, vars),
    onSuccess: async (): Promise<void> => {
      // Every declared resource, concurrently. `invalidateQueries` resolves once
      // the refetches it triggered have settled, and TanStack awaits this
      // callback before resolving `mutateAsync` — which is what gives `run` the
      // same "fresh before you continue" contract `await loadAll()` had.
      //
      // The keys come from the catalog rather than being built here, so the
      // prefix decision is testable in a renderer-less environment — see
      // mutationInvalidationKeys.
      //
      // TWO GUARDS BELOW, DOING TWO DIFFERENT JOBS. Neither may be dropped; see
      // "WHY THE INVALIDATION CANNOT FAIL THE WRITE" in the header.
      try {
        // `allSettled`, not `all`. `all` settles on the FIRST rejection, which
        // would let one failing key decide the callback while the entry declares
        // that all of them are invalidated every time. And the mapper is
        // `async` deliberately: that turns a SYNCHRONOUS throw out of
        // invalidateQueries into a rejected promise, so a throw on one key
        // cannot skip the keys after it in the array.
        const outcomes = await Promise.allSettled(
          mutationInvalidationKeys(entry.id).map(async (queryKey) => {
            await queryClient.invalidateQueries({queryKey});
          }),
        );
        const reasons = outcomes.flatMap((o) => (o.status === 'rejected' ? [o.reason] : []));
        if (reasons.length > 0) {
          warnInvalidationFailed(entry.id, `${reasons.length} of ${outcomes.length} declared invalidations`, reasons);
        }
      } catch (err) {
        // For everything that is NOT one of the per-key promises — today only
        // `mutationInvalidationKeys` itself, which cannot throw for an entry
        // that already resolved. That is precisely why it is caught: no future
        // edit to this callback may quietly reacquire the power to reject a
        // write that has already committed.
        warnInvalidationFailed(entry.id, 'its declared invalidations', [err]);
      }
    },
  };
}

/**
 * The one developer-visible trace of a swallowed invalidation.
 *
 * `console.warn`, not `console.error` and not a user-facing banner. The user's
 * save succeeded, and telling them otherwise is the defect this file exists to
 * remove — but a failure with no trace at all is how a fail-open survives, so
 * the id, the count and every reason are printed. layerBootLoad.ts warns on its
 * own degraded path for the same reason.
 */
function warnInvalidationFailed(mutationId: string, scope: string, reasons: readonly unknown[]): void {
  console.warn(
    `useRouteMutation: '${mutationId}' COMMITTED, but ${scope} failed. ` +
      'The write is saved. A resource with a live observer may show pre-write rows until its next mount.',
    ...reasons,
  );
}

/**
 * Run one catalog-declared write.
 *
 * `mutationId` is a runtime string for the same reason `resourceId` is: the
 * catalog is keyed by one, so nothing at this boundary knows which entry was
 * asked for. The two type parameters let a call site pin what it passes and
 * what it gets back without the catalog claiming a union it cannot narrow.
 */
export function useRouteMutation<Vars, Data>(
  mutationId: string,
  {client = sb}: UseRouteMutationOptions = {},
): UseRouteMutationReturn<Vars, Data> {
  const queryClient = useQueryClient();

  // `error` is held here rather than read off the mutation result because
  // `mutateAsync` REJECTS as well as recording, and a call site that catches
  // the rejection would otherwise leave a stale error sitting in the returned
  // projection. One owner, cleared on each new attempt.
  const [error, setError] = useState<unknown>(null);

  const mutation = useMutation(routeMutationOptions<Vars, Data>({mutationId, client, queryClient}));

  // Latest-ref, for the same reason useRouteResource's `refetch` uses one: call
  // sites hold these in closures and hand them to controls, and an identity
  // that churned on every settle would be a new prop on every render. Written
  // during render but read only from event handlers.
  const mutateRef = useRef<((vars: Vars) => Promise<Data>) | null>(null);
  mutateRef.current = mutation.mutateAsync;

  const run = useCallback(async (vars: Vars) => {
    setError(null);
    try {
      // Non-null assertion rather than a fallback, DELIBERATELY — same call as
      // useRouteResource's refetch makes and for the same reason. The ref is
      // assigned on every render above, and this callback is only reachable
      // from an event handler, so it has been written at least once by the time
      // it can run. A `?? Promise.resolve()` guard would convert a genuine
      // "mutation was never wired" bug into a silently successful write.
      return await mutateRef.current!(vars);
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    mutation.reset();
    // `mutation.reset` is stable across renders in TanStack v5; it is listed so
    // the lint rule sees the read rather than being silenced.
  }, [mutation]);

  return useMemo(() => ({run, pending: mutation.isPending, error, reset}), [run, mutation.isPending, error, reset]);
}
